import { FileText, Landmark } from "lucide-react";

import { cn } from "../../../lib/utils";
import { Button } from "../../ui/button";

export interface InputVerificationCTAsProps {
  // Caller owns Plus/free routing; this component is presentational.
  // At the section-header (variant="primary"), the parent's handler is
  // intelligent — it decides which Plaid Link modes to chain based on
  // which products are already linked, so the user only ever sees one
  // affordance ("Connect bank") and never re-runs a flow they've
  // already completed. Per-row (variant="compact") handlers are tied to
  // a single row's product.
  onConnectBank: () => void;
  onUploadDocs: () => void;
  // Two visual densities so the same component reads naturally as a
  // prominent section-header pair OR as inline siblings to a "+ Add" row.
  variant?: "primary" | "compact";
  hierarchy?: "default" | "verification-strength";
  // When true, the Connect bank affordance renders as a non-clickable
  // "Coming soon" pill instead of a Button. Used to defer surfaces whose
  // backing Plaid product is parked (currently: Bank Income — see the
  // legacy-vs-Plaid-Check note in backend gateway_plaid.go). The Upload
  // documents button is unaffected so the user still has a valid
  // verification path.
  bankComingSoon?: boolean;
  className?: string;
}

// InputVerificationCTAs renders the verification affordances ("Connect bank"
// + "Upload documents") as a paired CTA group. Used in two placements on the
// Profile page:
//   - Section-header (variant="primary"): above the inputs rows, prominent.
//   - Per-row inline (variant="compact"): next to each "+ Add …" button so
//     a user editing a row can verify without scrolling to the header.
// Both placements share the same click semantics so the user can take the
// verification step from wherever they are on the page.
export function InputVerificationCTAs({
  onConnectBank,
  onUploadDocs,
  variant = "primary",
  hierarchy = "default",
  bankComingSoon = false,
  className,
}: InputVerificationCTAsProps) {
  const isPrimary = variant === "primary";
  // SILL-338: verification-strength hierarchy lets per-row inline CTAs adopt
  // the section-header's strong visual weight (default button + larger sizes)
  // so the BANK affordance reads as the primary verification path even when
  // it lives in a compact row.
  const useStrongHierarchy = isPrimary || hierarchy === "verification-strength";
  const size = isPrimary ? "default" : "sm";
  const iconClassName = isPrimary ? "h-4 w-4" : "h-3.5 w-3.5";
  return (
    <div
      data-testid={`input-verification-ctas-${variant}`}
      className={cn(
        isPrimary
          ? "flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3"
          : "flex flex-wrap items-center gap-2",
        className,
      )}
    >
      {bankComingSoon ? (
        <span
          data-testid="input-verification-cta-connect-bank-coming-soon"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-md border border-dashed border-border bg-muted/40 text-muted-foreground",
            isPrimary ? "px-3 py-2 text-sm" : "px-2.5 py-1.5 text-xs",
          )}
          aria-label="Bank income coming soon"
        >
          <Landmark className={iconClassName} aria-hidden="true" />
          Connect bank — coming soon
        </span>
      ) : (
        <Button
          type="button"
          data-testid="input-verification-cta-connect-bank"
          variant={useStrongHierarchy ? "default" : "outline"}
          size={size}
          onClick={onConnectBank}
          className="gap-1.5"
        >
          <Landmark className={iconClassName} aria-hidden="true" />
          Connect bank
        </Button>
      )}
      <Button
        type="button"
        data-testid="input-verification-cta-upload-docs"
        variant={useStrongHierarchy ? "outline" : "ghost"}
        size={size}
        onClick={onUploadDocs}
        className="gap-1.5"
      >
        <FileText className={iconClassName} aria-hidden="true" />
        Upload documents
      </Button>
    </div>
  );
}
